import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationInterface } from "../interfaces";
import CustomButton from "../../../components/CustomButton";
import AlertIcon from "../../../components/Icons/AlertIcon";
import {scrollTop} from "../../../helpers/scrolling";
import Chip from "../../../components/Boxes/Chip";
import {getLanguage} from "../../../lang/helpers";

interface Props {
    application: ApplicationInterface;
    save: Function
}

const allOptions: Array<string> = [
    "cs",
    "da",
    "nl",
    "fi",
    "fr",
    "en",
    "de",
    "el",
    "hu",
    "it",
    "no",
    "pl",
    "pt",
    "es",
    "sv",
]

const StepFour: React.FC<Props> = (props: Props) => {
    const translation = useTranslation();
    const { t, i18n } = translation;
    const lang = getLanguage(i18n.language);
    const [languagesNative, setLanguagesNative] = useState<Array<string>>(props.application.languagesNative);
    const [languagesProfessional, setLanguagesProfessional] = useState<Array<string>>(props.application.languagesProfessional);
    const [additionalLanguages, setAdditionalLanguages] = useState<string>(props.application.additionalLanguages)
    const [errors, setErrors] = useState<number>(0)
    const [triedSave, setTriedSave] = useState<boolean>(false)
    const [options, setOptions] = useState<Array<string>>(allOptions)

    useEffect(() => {
        scrollTop()
    }, [])

    useEffect(() => {
        updateOptions()
        if (triedSave) {
            checkInput()
        }
    }, [languagesNative, languagesProfessional])

    const checkInput = () => {
        let _errors = 0
        if (languagesNative.length < 1 && languagesProfessional.length < 1) {
            _errors++
        }
        setErrors(_errors)
        return _errors
    }

    const save = () => {
        setTriedSave(true)
        if (checkInput() > 0) {
            console.log('There are errors')
            return
        }
        const _application: ApplicationInterface = props.application
        _application.languagesNative = languagesNative
        _application.languagesProfessional = languagesProfessional
        _application.additionalLanguages = additionalLanguages
        props.save(_application)
    }

    const addLanguage = (lang: string, native: boolean) => {
        const _languages = native ? [...languagesNative] : [...languagesProfessional]
        _languages.push(lang)
        if (native) {
            setLanguagesNative(_languages)
        } else {
            setLanguagesProfessional(_languages)
        }
    }

    const removeLanguage = (lang: string, native: boolean) => {
        const _languages = native ? languagesNative : languagesProfessional
        const _newLanguages: Array<string> = []
        for (const _language of _languages) {
            if (lang !== _language) {
                _newLanguages.push(_language)
            }
        }
        if (native) {
            setLanguagesNative(_newLanguages)
        } else {
            setLanguagesProfessional(_newLanguages)
        }
    }

    const updateOptions = () => {
        const _options: Array<string> = []
        for (const option of allOptions) {
            if (languagesNative.indexOf(option) < 0 && languagesProfessional.indexOf(option) < 0) {
                _options.push(option)
            }
        }
        setOptions(_options)
    }

    return (
      <>
          <div className="mt-6 text-xl font-medium">{t('languages')}</div>

          <div className="mt-4 text-neutral-400">
              {lang === 'sv' ? 'Välj de språk du känner dig bekväm att coacha på nedan.' :
                'Please choose the languages that you feel confident to coach in under the proficiency levels below.'
              }

          </div>

          <div className="mt-6 font-medium">
              {lang === 'sv' ? 'Modersmål': 'Native'}
          </div>

          <select value={""}
                  onChange={(e) => addLanguage(e.target.value, true)}
                  className="z-select mt-2">
              <option value="">{t("select")}</option>
              {
                  options.map((option, index) => (
                    <option value={option} key={"native_" + index}>{t(option)}</option>
                  ))
              }
          </select>

          <div className="flex flex-wrap items-center">
              {languagesNative.map((language, index) => (
                <Chip key={"native_" + index} txt={t(language)} remove={() => removeLanguage(language, true)}/>
              ))}
          </div>

          <div className="mt-6 font-medium">
              {lang === 'sv' ? 'Professionell': 'Professional'}
          </div>

          <select value={""}
                  onChange={(e) => addLanguage(e.target.value, false)}
                  className="z-select mt-2">
              <option value="">{t("select")}</option>
              {
                  options.map((option, index) => (
                    <option value={option} key={"professional_" + index}>{t(option)}</option>
                  ))
              }
          </select>

          <div className="flex flex-wrap items-center">
              {languagesProfessional.map((language, index) => (
                <Chip key={"prof_" + index} txt={t(language)} remove={() => removeLanguage(language, false)}/>
              ))}
          </div>

          <div className="mt-6">
              {lang === 'sv' ? 'Andra språk du kan coacha på' : 'Other languages you can coach in'}
          </div>
          <input value={additionalLanguages}
                 className="z-input mt-2"
                 onChange={(e) => setAdditionalLanguages(e.target.value)}/>

          {
            (triedSave && errors > 0) &&
              <div className="mt-6 p-4 bg-neutral-700 border rounded-md border-cherry-500 flex items-center">
                  <AlertIcon aria-label="Error" className="text-cherry-500!"/>
                  <div className="ml-2 font-medium">
                      {lang === 'sv' ? 'Välj minst ett språk under modersmål eller professionell' :
                        'Please select at least one native or professional language'}

                  </div>
              </div>
          }


          <CustomButton className="mt-6" onClick={() => save()}>
              {t('next')}
          </CustomButton>
      </>
    )
}

export default StepFour